import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Video,
  Share2,
  Check,
  ArrowRight,
  Play,
  ChevronDown,
  ChevronUp,
  Scan,
  Eye,
  X,
  Ruler,
  CircleDot,
  Zap,
  Shapes,
  Move,
  Sparkles,
} from 'lucide-react';
import VTOWidget from './components/VTOWidget';
import FeatureCard from './components/FeatureCard';
import SiteHeader from './components/SiteHeader';
import SiteFooter from './components/SiteFooter';
import s1TryOn from './assets/images/s1.png?v=1.1';
import s2RecordShare from './assets/images/s2.png?v=1.1';
import s3Pupillary from './assets/images/s3.png';
import s4Couple from './assets/images/s4.png';
import mojenaocaleLogo from './assets/branding/mojenaocale-logo.webp';
import visionLiveLogo from './assets/images/visionlive.png';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

// Demo Modal Component
const DemoModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center"
      >
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Modal Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full h-full max-w-6xl max-h-[90vh] m-4 bg-gray-900 rounded-3xl overflow-hidden shadow-2xl"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          {/* VTO Widget Component */}
          <div className="w-full h-full">
            <VTOWidget embedded={true} />
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Phone Mockup Component
const PhoneMockup = ({ children, className = '', glow = false }) => {
  return (
    <div className={`relative ${className}`}>
      {glow && (
        <div className="absolute inset-0 bg-[#3B82F6]/20 rounded-[3rem] blur-3xl scale-110" />
      )}
      <div className="relative bg-[#1a1a1a] rounded-[3rem] p-3 shadow-2xl shadow-black/50 border border-white/10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-[#1a1a1a] rounded-b-2xl z-10" />
        <div className="relative bg-gray-100 rounded-[2.5rem] overflow-hidden aspect-[9/19]">
          {children}
        </div>
      </div>
    </div>
  );
};

// Hero Section
const HeroSection = ({ onOpenDemo }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    const video = videoRef.current;
    if (!el || !video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        if (!isMobile) return;

        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.5, rootMargin: '0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  return (
    <section className="relative pt-10 sm:pt-14 md:pt-32 pb-20 overflow-hidden min-h-screen bg-[#0d0d0d]">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d] via-[#111111] to-[#0d0d0d]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="min-w-0"
          >
            <motion.div
              variants={fadeInUp}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6"
            >
              <span className="w-2 h-2 rounded-full bg-[#3B82F6] animate-pulse" />
              <span className="text-sm font-medium text-white/80">Now available for all platforms</span>
            </motion.div>

            <motion.h1
              variants={fadeInUp}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[0.9] tracking-tight mb-6"
            >
              The most <div className="text-[#3B82F6]">converting</div>{' '}
              virtual try-on{' '}
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="text-lg text-white/60 max-w-lg mb-8 leading-relaxed break-words min-w-0"
            >
              Customers try on eyewear virtually and share it on social media. Zero latency.
              <br />
              Maximum conversion.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row sm:items-center gap-4 mb-12"
            >
              <div className="flex flex-col gap-4 w-full sm:w-auto">
                <div className="flex flex-wrap items-center gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onOpenDemo}
                    className="px-8 py-4 rounded-full bg-[#3B82F6] text-base font-semibold text-white hover:bg-[#2563EB] transition-all flex items-center gap-2"
                  >
                    Try Demo Now
                    <ArrowRight className="w-4 h-4" />
                  </motion.button>
                  <a
                    href="#pricing"
                    className="text-base font-semibold text-white/70 hover:text-white transition-colors"
                  >
                    View pricing
                  </a>
                </div>
                <div className="flex flex-col items-center gap-2 sm:hidden">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={onOpenDemo}
                    className="px-8 py-4 rounded-full bg-transparent text-base font-semibold text-white border border-white/20 hover:border-white/40 transition-all flex items-center gap-2"
                  >
                    <Play className="w-4 h-4 fill-white text-white" />
                    Watch Demo
                  </motion.button>
                  <motion.div
                    animate={{ y: [0, 6, 0] }}
                    transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
                    className="text-white/50"
                  >
                    <ChevronDown className="w-6 h-6" />
                  </motion.div>
                </div>
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onOpenDemo}
                className="hidden sm:flex px-8 py-4 rounded-full bg-transparent text-base font-semibold text-white border border-white/20 hover:border-white/40 transition-all items-center gap-2"
              >
                <Play className="w-4 h-4 fill-white text-white" />
                Watch Demo
              </motion.button>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="mb-10 flex w-full max-w-xl items-center gap-6"
            >
              <div className="flex items-center gap-2 text-sm text-white/50">
                <Check className="w-4 h-4 text-green-400" />
                <span>No setup fees</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/50">
                <Check className="w-4 h-4 text-green-400" />
                <span>Up and running in 30 min</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/50">
                <Check className="w-4 h-4 text-green-400" />
                <span>Cancel anytime</span>
              </div>
            </motion.div>

          </motion.div>

          <motion.div
            ref={containerRef}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative w-full flex justify-center lg:justify-center max-lg:max-w-[18rem] max-lg:mx-auto lg:max-w-none lg:mx-0"
          >
            <PhoneMockup className="w-72 md:w-80 shrink-0" glow>
              <video
                ref={videoRef}
                src={`${process.env.PUBLIC_URL || ''}/promov2.mp4?v=1`}
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                className="absolute inset-0 w-full h-full object-cover"
                style={{ pointerEvents: 'none' }}
                onError={(e) => {
                  console.error('Video error:', e);
                  console.error('Video src:', e.target.src);
                }}
                onLoadedData={() => {
                  console.log('Video loaded successfully');
                  if (videoRef.current) {
                    videoRef.current.play().catch(() => {});
                  }
                }}
              />
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5 }}
                onClick={onOpenDemo}
                className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors cursor-pointer flex items-center justify-center group"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 2, type: 'spring' }}
                  className="w-16 h-16 rounded-full bg-white/90 shadow-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Play className="w-6 h-6 text-[#3B82F6] fill-[#3B82F6] ml-1" />
                </motion.div>
              </motion.div>
            </PhoneMockup>

            <div className="hidden md:flex flex-col absolute right-6 top-1/4 gap-[5.5rem]">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 }}
                className="flex bg-[#1a1a1a] border border-white/10 rounded-2xl shadow-2xl p-4 items-center gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">Customer converted</p>
                  <p className="text-xs text-white/50">Just now</p>
                </div>
              </motion.div>
            </div>
            <div className="hidden md:flex flex-col absolute right-8 top-12 gap-[5.5rem]">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 }}
                className="flex bg-[#1a1a1a] border border-white/10 rounded-2xl shadow-2xl p-4 items-center gap-3"
              >
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-xl leading-none select-none">
                  <span className="inline-block transform scale-110" aria-hidden>👌</span>
                </div>
                <div>
                  <p className="font-semibold text-white text-sm">Fits perfectly!</p>
                  <p className="text-xs text-white/50">2 min ago</p>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1 }}
              className="hidden md:block absolute left-8 bottom-1/4 bg-[#1a1a1a] border border-white/10 rounded-2xl shadow-2xl p-4"
            >
              <div className="flex items-center gap-2 mb-2">
                <Share2 className="w-4 h-4 text-[#3B82F6]" />
                <span className="text-sm font-medium text-white">Shared to Stories</span>
              </div>
              <p className="text-xs text-white/50">12 shares today</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// 2×2 feature grid: image paths (crop source art into these four; see public/images/README.md)
// Each feature uses a contextual icon (Lucide) relevant to the sentence.
const featureCardData = [
  {
    id: 1,
    tag: 'VisionLive',
    tagIcon: Sparkles,
    title: 'Let customers try before they buy',
    description: 'Our virtual try-on makes shopping seamless and conversion-focused: customers see frames on their face in real time, reducing returns and building confidence.',
    features: [
      { text: 'Customers try virtual frames live', icon: Eye },
      { text: 'Real-time face tracking and scaling', icon: Scan },
    ],
    buttonText: 'Learn more',
    backgroundImage: s1TryOn,
  },
  {
    id: 2,
    tag: 'VisionLive',
    tagIcon: Sparkles,
    title: 'Record and share',
    description: 'Record try-on videos and share to Stories, social, or support. One place to capture demos, feedback, and customer enquiries.',
    features: [
      { text: 'Record try-on sessions as video', icon: Video },
      { text: 'Share to social or support', icon: Share2 },
    ],
    buttonText: 'Learn more',
    backgroundImage: s2RecordShare,
  },
  {
    id: 3,
    tag: 'VisionLive',
    tagIcon: Sparkles,
    title: 'Automatic pupillary distance',
    description: 'AI measures pupillary distance from the camera—no ruler or optician needed. Your customers get accurate PD and a smooth path to purchase.',
    features: [
      { text: 'Automatic pupillary distance', icon: Ruler },
      { text: 'Pupil-to-pupil and frame fit', icon: CircleDot },
      { text: 'Built into the experience', icon: Zap },
    ],
    buttonText: 'Learn more',
    backgroundImage: s3Pupillary,
  },
  {
    id: 4,
    tag: 'VisionLive',
    tagIcon: Sparkles,
    title: (
      <>
        If they fit. <br /><span className="text-[#3B82F6]">They fit.</span>
      </>
    ),
    description: 'Professional-grade positioning and sizing so the fit customers see matches the eyewear they receive—fewer returns, more trust.',
    features: [
      { text: 'Consistent fit across face shapes', icon: Shapes },
      { text: 'Precise frame position and sizing', icon: Move },
      { text: 'Built into the experience', icon: Zap },
    ],
    backgroundImage: s4Couple,
  },
];

// Features Section (2×2 image-backed cards)
const FeaturesSection = () => {
  return (
    <section id="features" className="py-24 bg-[#0d0d0d]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block text-sm font-semibold text-[#3B82F6] mb-4 uppercase tracking-wide">
            Features
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            Everything you need to convert visitors into buyers
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            A complete virtual try-on solution that increases engagement, reduces returns, and drives sales.
          </p>
        </motion.div>

        <div className="space-y-8 md:space-y-12 max-w-7xl w-full">
          {featureCardData.map((card, index) => (
            <FeatureCard
              key={card.id}
              {...card}
              index={index}
              align={index % 2 === 0 ? 'left' : 'right'}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// No 3D models? No problem. 2D-to-3D full-width section
const No3DModelsSection = ({ onCTA }) => {
  return (
    <section className="py-20 md:py-28 bg-[#0d0d0d] relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#3B82F6]/5 to-transparent" />
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
          But you don&apos;t have 3D models of your eyewear?
          </h2>
          <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#3B82F6] mb-6">
            You don't need to ...
          </p>
          <p className="text-lg sm:text-xl text-white/70 max-w-3xl mx-auto mb-10 leading-relaxed">
            We use your 2D photos to create a 3D try-on experience for your visitors/clients.
          </p>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={onCTA}
              className="px-8 py-4 rounded-full bg-[#3B82F6] text-base font-semibold text-white hover:bg-[#2563EB] transition-colors flex items-center gap-2 shadow-lg shadow-[#3B82F6]/25"
            >
              Get started with 2D photos
              <ArrowRight className="w-4 h-4" />
            </motion.button>
            <a
              href="#pricing"
              className="text-white/70 hover:text-white font-medium transition-colors text-sm sm:text-base"
            >
              View pricing →
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const PricingSection = ({ onGetStarted }) => {
  const [isAnnual, setIsAnnual] = useState(false);

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      description: 'Perfect for small eyewear retailers',
      monthlyPrice: 249,
      annualPrice: 199,
      annualTotal: 2388,
      annualPeriodLabel: 'year',
      features: [
        '150 active products',
        '10,000 monthly VTO sessions',
        'Integration support',
        'Backoffice access',
        'Email support',
      ],
      cta: 'Get started',
      href: '/auth?plan=starter',
    },
    {
      id: 'advanced',
      name: 'Advanced',
      description: 'For growing businesses',
      monthlyPrice: 499,
      annualPrice: 379,
      annualTotal: 2274,
      annualPeriodLabel: '6 months',
      popular: true,
      features: [
        '500 active products',
        '100,000 monthly VTO sessions',
        'Integration support',
        'Backoffice access',
        'Priority support',
        'Custom branding',
      ],
      cta: 'Get started',
      href: '/auth?plan=advanced',
    },
    {
      id: 'business',
      name: 'Business',
      description: 'Enterprise solution',
      monthlyPrice: null,
      features: [
        'Unlimited products',
        'Unlimited VTO sessions',
        'Dedicated integration support',
        'Custom SLAs & onboarding',
        'Dedicated success manager',
        'API access & webhooks',
      ],
      cta: 'Contact sales',
      href: 'mailto:sales@vltryon.com',
    },
  ];

  return (
    <section id="pricing" className="py-24 bg-[#0d0d0d] overflow-hidden">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block text-sm font-semibold text-[#3B82F6] mb-4 uppercase tracking-wide">
            Pricing
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto mb-8">
            Start with a monthly or discounted plan. Cancel anytime.
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-3 bg-[#1a1a1a] rounded-full p-1 border border-white/10">
            <button
              onClick={() => setIsAnnual(false)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                !isAnnual ? 'bg-white text-[#0d0d0d]' : 'text-white/60 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsAnnual(true)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                isAnnual ? 'bg-white text-[#0d0d0d]' : 'text-white/60 hover:text-white'
              }`}
            >
              Discounted
              <span className="text-xs font-semibold text-emerald-500">Save up to 24%</span>
            </button>
          </div>
        </motion.div>

        <div className="grid gap-6 md:grid-cols-3">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              className={`relative rounded-[1.75rem] border p-8 flex flex-col ${
                plan.popular
                  ? 'border-[#3B82F6]/50 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.12),transparent_60%),#161616]'
                  : 'border-white/10 bg-[#161616]'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center rounded-full bg-[#3B82F6] px-4 py-1 text-xs font-semibold text-white">
                    Most popular
                  </span>
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
                <p className="text-sm text-white/50">{plan.description}</p>
              </div>

              {plan.monthlyPrice !== null ? (
                <div className="mb-6">
                  <div className="flex items-end gap-1">
                    <span className="text-4xl font-bold text-white">
                      ${isAnnual ? plan.annualPrice : plan.monthlyPrice}
                    </span>
                    <span className="text-white/50 mb-1.5">/mo</span>
                  </div>
                  {isAnnual && (
                    <p className="text-xs text-white/40 mt-1">
                      Billed ${plan.annualTotal.toLocaleString()}/{plan.annualPeriodLabel}
                    </p>
                  )}
                </div>
              ) : (
                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">Custom</span>
                  <p className="text-sm text-white/50 mt-1">Contact for pricing</p>
                </div>
              )}

              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2.5 text-sm text-white/70">
                    <Check className="w-4 h-4 text-[#3B82F6] shrink-0 mt-0.5" />
                    {feature}
                  </li>
                ))}
              </ul>

              <a
                href={plan.href}
                className={`w-full py-3.5 rounded-xl font-semibold text-sm text-center transition-all ${
                  plan.popular
                    ? 'bg-[#3B82F6] text-white hover:bg-[#2563EB]'
                    : plan.id === 'business'
                    ? 'border border-white/20 text-white hover:border-white/40 hover:bg-white/5'
                    : 'bg-white/10 text-white hover:bg-white/15'
                }`}
              >
                {plan.cta}
              </a>
            </motion.div>
          ))}
        </div>

        {/* Live proof */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-white/50"
        >
          <a
            href="https://mojenaocale.ba"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 hover:text-white/80 transition-colors group"
          >
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/95 p-1.5 shadow">
              <img src={mojenaocaleLogo} alt="mojenaocale.ba" className="max-h-full w-auto object-contain" />
            </div>
            <span>Live on mojenaocale.ba — 300+ frames, +45% conversion lift</span>
            <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Partnership / Live proof section
const PartnershipSection = () => {
  return (
    <section id="partnership" className="py-24 bg-[#0d0d0d] overflow-hidden">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block text-sm font-semibold text-[#3B82F6] mb-4 uppercase tracking-wide">
            Live in production
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            See it in action
          </h2>
          <p className="text-lg text-white/60 max-w-3xl mx-auto">
            VisionLive is live in a real eyewear store, converting real customers every day.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative w-full overflow-hidden rounded-[2rem] border border-[#3B82F6]/20 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.22),transparent_42%),linear-gradient(160deg,#111827_0%,#0d0d0d_60%,#111827_100%)] p-8 md:p-10"
        >
          <div className="absolute -right-20 top-0 h-56 w-56 rounded-full bg-[#3B82F6]/10 blur-3xl" />
          <div className="relative z-10">
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-4">
                <div className="rounded-2xl px-1 py-1">
                  <img src={visionLiveLogo} alt="VisionLive" className="h-8 w-auto object-contain" />
                </div>
                <div className="text-white/30 text-xl font-light">×</div>
                <div className="rounded-2xl bg-white px-4 py-3 shadow-xl shadow-black/20">
                  <img src={mojenaocaleLogo} alt="MojeNaocale.ba" className="h-8 w-auto object-contain" />
                </div>
              </div>
              <span className="inline-flex w-fit items-center rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-emerald-300">
                Active launch collaboration
              </span>
            </div>

            <div className="mt-10 grid gap-6 md:grid-cols-[1.05fr_0.95fr]">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#93C5FD] mb-4">
                  Real results, live store
                </p>
                <h3 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-4">
                  VisionLive powers the virtual try-on experience at mojenaocale.ba.
                </h3>
                <p className="text-white/65 text-base md:text-lg leading-relaxed mb-8">
                  A full eyewear catalog with hundreds of frames — customers try on in real time, share to social, and convert at a measurably higher rate.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="https://mojenaocale.ba"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#3B82F6] px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#2563EB]"
                  >
                    Visit mojenaocale.ba
                    <ArrowRight className="w-4 h-4" />
                  </a>
                  <a
                    href="#pricing"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:border-white/30 hover:bg-white/10"
                  >
                    View pricing
                  </a>
                </div>
              </div>

              <div className="rounded-[1.75rem] border border-white/10 bg-black/20 p-6 backdrop-blur-sm">
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-white/45 mb-5">
                  Live partnership results
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                    <p className="text-2xl font-bold text-white mb-1">300+</p>
                    <p className="text-sm text-white/50">Frames in active catalog</p>
                  </div>
                  <div className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                    <p className="text-2xl font-bold text-white mb-1">+45%</p>
                    <p className="text-sm text-white/50">Conversion lift recorded</p>
                  </div>
                  <div className="col-span-2 rounded-2xl border border-white/8 bg-white/[0.03] p-4">
                    <p className="text-sm text-white/65 leading-relaxed">
                      Real customer traffic, real purchases — proof that virtual try-on converts.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'What is the Virtual Try-On? How does it work?',
      answer: 'Virtual Try-On uses advanced AR technology and facial recognition to let customers see how eyewear looks on their face in real-time through their device camera. It works directly in the browser with no app download required.',
    },
    {
      question: 'Can we test a small product catalog for a limited time?',
      answer: 'Yes! Reach out to us and we can arrange an evaluation period so you can see the impact on your conversion rates before committing to a full plan.',
    },
    {
      question: 'How long does it take to set up on the e-commerce website?',
      answer: 'Setup typically takes less than 30 minutes. Just add our script tag to your website, and our team will help configure your product catalog.',
    },
    {
      question: 'How does the Virtual Try-On work on the website?',
      answer: 'Customers click the "Try On" button on any product page. Their camera activates, and they can see themselves wearing the selected frames in real-time, record videos, and share to social media.',
    },
    {
      question: 'Is the Virtual Try-On compatible with my platform?',
      answer: 'VisionLive works with all major e-commerce platforms including Shopify, WooCommerce, Magento, BigCommerce, and custom-built stores. Our simple JavaScript integration works anywhere.',
    },
  ];

  return (
    <section id="faq" className="py-20 bg-[#0d0d0d]">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Frequently Asked Questions
          </h2>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="bg-[#1a1a1a] rounded-2xl border border-white/10 overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left"
              >
                <span className="font-semibold text-white pr-8">{faq.question}</span>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-[#3B82F6] shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-white/40 shrink-0" />
                )}
              </button>
              <motion.div
                initial={false}
                animate={{
                  height: openIndex === index ? 'auto' : 0,
                  opacity: openIndex === index ? 1 : 0,
                }}
                transition={{
                  height: { type: 'tween', duration: 0.35, ease: [0.4, 0, 0.2, 1] },
                  opacity: { type: 'tween', duration: 0.25, ease: 'easeInOut' },
                }}
                className="overflow-hidden"
              >
                <div className="px-6 pb-5">
                  <p className="text-white/60 leading-relaxed">{faq.answer}</p>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
// const CTASection = () => {
//   return (
//     <section className="py-20 bg-[#0d0d0d]">
//       <div className="max-w-6xl mx-auto px-6">
//         <motion.div
//           initial={{ opacity: 0, scale: 0.95 }}
//           whileInView={{ opacity: 1, scale: 1 }}
//           viewport={{ once: true }}
//           className="relative rounded-[2.5rem] overflow-hidden bg-[#1a1a1a] border border-white/10 p-12 md:p-16"
//         >
//           {/* Background Elements */}
//           <div className="absolute top-0 right-0 w-96 h-96 bg-[#3B82F6]/10 rounded-full blur-3xl" />
//           <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#3B82F6]/5 rounded-full blur-3xl" />
          
//           <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
//             <div>
//               <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
//                 How can we make your shopping experience more effective? Let's talk
//               </h2>
//               <p className="text-white/50 mb-8">
//                 Book a demo with our team and see how VisionLive can transform your e-commerce experience.
//               </p>
//               <div className="flex flex-col sm:flex-row gap-4">
//                 <motion.button
//                   whileHover={{ scale: 1.02 }}
//                   whileTap={{ scale: 0.98 }}
//                   className="px-8 py-4 rounded-full bg-[#3B82F6] text-base font-semibold text-white hover:bg-[#2563EB] transition-colors"
//                 >
//                   Book a Demo
//                 </motion.button>
//                 <motion.button
//                   whileHover={{ scale: 1.02 }}
//                   whileTap={{ scale: 0.98 }}
//                   className="px-8 py-4 rounded-full bg-transparent text-base font-semibold text-white border border-white/20 hover:border-white/40 transition-colors"
//                 >
//                   Contact Sales
//                 </motion.button>
//               </div>
//             </div>
            
//             <div className="hidden md:flex justify-center">
//               <div className="w-64 h-64 rounded-full bg-[#3B82F6]/10 flex items-center justify-center">
//                 <div className="w-48 h-48 rounded-full bg-[#3B82F6]/20 flex items-center justify-center">
//                   <Eye className="w-16 h-16 text-[#3B82F6]/60" />
//                 </div>
//               </div>
//             </div>
//           </div>
//         </motion.div>
//       </div>
//     </section>
//   );
// };

// Demo Section
const DemoSection = ({ onOpenDemo }) => {
  return (
    <section className="py-20 bg-[#3B82F6]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Get started.
              <br />
              Try our demo now
            </h2>
            <p className="text-white/80 mb-6">
              Experience the power of virtual try-on technology firsthand. 
              No signup required.
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onOpenDemo}
              className="px-8 py-4 rounded-full bg-[#0d0d0d] text-white font-semibold hover:bg-[#1a1a1a] transition-colors flex items-center gap-2"
            >
              Launch Demo
              <ArrowRight className="w-4 h-4" />
            </motion.button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex justify-center"
          >
            <PhoneMockup className="w-56">
              {/* Promo video */}
              <video
                src="/promo.mp4"
                autoPlay
                muted
                loop
                playsInline
                className="absolute inset-0 w-full h-full object-cover"
                aria-label="Virtual Try-On Demo"
              />
              <div
                onClick={onOpenDemo}
                className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors cursor-pointer flex items-center justify-center group"
              >
                <div className="w-14 h-14 rounded-full bg-[#0d0d0d] shadow-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                </div>
              </div>
            </PhoneMockup>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Main Landing Page Component
const LandingPage = ({ onNavigateToDemo, onNavigateToOutfit, onNavigateToBlog, onNavigateToDashboard, onNavigateToTerms, onNavigateToPrivacy, onNavigateToCookiePolicy, onNavigateToApiDocs }) => {
  const [isDemoOpen, setIsDemoOpen] = useState(false);

  const openDemo = () => setIsDemoOpen(true);
  const closeDemo = () => setIsDemoOpen(false);

  // Use navigation for "Try it out" button, fallback to modal if no navigation prop
  const handleTryItOut = onNavigateToDemo || openDemo;
  
  // Handle outfit navigation
  const handleOutfitClick = onNavigateToOutfit || (() => {
    window.location.href = '/outfit';
  });

  // Handle blog navigation
  const handleBlogClick = onNavigateToBlog || (() => {
    window.location.href = '/blog';
  });

  // Handle login/dashboard navigation
  const handleLoginClick = onNavigateToDashboard || (() => {
    window.location.href = '/dashboard';
  });

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white font-sans antialiased">
      <SiteHeader onOpenDemo={handleTryItOut} onOutfitClick={handleOutfitClick} onBlogClick={onNavigateToBlog ? handleBlogClick : undefined} onLoginClick={handleLoginClick} />
      <HeroSection onOpenDemo={handleTryItOut} />
      <FeaturesSection />
      <No3DModelsSection onCTA={handleTryItOut} />
      <PricingSection onGetStarted={handleTryItOut} />
      <PartnershipSection />
      <FAQSection />
      {/* <CTASection /> */}
      <DemoSection onOpenDemo={handleTryItOut} />
      <SiteFooter
        onNavigateToTerms={onNavigateToTerms}
        onNavigateToPrivacy={onNavigateToPrivacy}
        onNavigateToCookiePolicy={onNavigateToCookiePolicy}
        onNavigateToApiDocs={onNavigateToApiDocs}
        onNavigateToBlog={onNavigateToBlog}
      />
      
      {/* Demo Modal - only used when no navigation prop */}
      {!onNavigateToDemo && <DemoModal isOpen={isDemoOpen} onClose={closeDemo} />}
    </div>
  );
};

export default LandingPage;
